import { useEffect, useState } from "react";
import axios from "axios";
import { API, fmtErr, money } from "../lib/pos";
import { CheckCircle2, Clock, Phone } from "lucide-react";

/* Draft Tickets — cashier + manager. Lists pending customer drafts.
   Clicking "Open in POS" converts and takes over the created order. */
export function DraftTickets({ refresh, onOpenInPos }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const r = await axios.get(`${API}/drafts`); setRows(r.data || []); }
    catch (e) { setErr(fmtErr(e)); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const openInPos = (draft) => {
    onOpenInPos?.(draft);
  };

  const now = Date.now();

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">CUSTOMER DRAFTS</p>
          <h1>Draft tickets <small className="muted" style={{ fontSize: 14 }}>· {rows.length} awaiting pickup</small></h1>
          <p className="muted">Customers who placed a draft from their phone. Open in POS to bill.</p>
        </div>
      </div>
      {err && <div className="auth-error" data-testid="draft-error">{err}</div>}
      {rows.length === 0 && <div className="empty" style={{ marginTop: 20 }}><b>No drafts right now</b><small>Drafts show up here the moment a customer submits from the /order page. They auto-expire after 10 minutes.</small></div>}
      <div className="draft-grid" data-testid="draft-grid">
        {rows.map((d) => {
          const exp = d.expires_at ? new Date(d.expires_at).getTime() : 0;
          const left = Math.max(0, Math.floor((exp - now) / 1000));
          const mm = String(Math.floor(left / 60)).padStart(2, "0");
          const ss = String(left % 60).padStart(2, "0");
          const items = d.items || [];
          return (
            <div key={d.draft_number} className="draft-card" data-testid={`draft-card-${d.draft_number}`}>
              <div className="draft-hd">
                <b>#{d.draft_number}</b>
                <span className={`draft-timer ${left < 120 ? "danger" : ""}`}><Clock size={11} /> {mm}:{ss}</span>
              </div>
              <div className="draft-cust">
                <b>{d.customer_name}</b>
                <small><Phone size={10} style={{ verticalAlign: "middle", marginRight: 3 }} />{d.customer_phone}</small>
              </div>
              <ul className="draft-items">
                {items.slice(0, 6).map((it, i) => (
                  <li key={i}><b>{it.quantity}×</b> {it.name}{it.note ? <em> — {it.note}</em> : null}</li>
                ))}
                {items.length > 6 && <li className="muted">+ {items.length - 6} more</li>}
              </ul>
              <div className="draft-foot">
                <span>{money(d.subtotal || 0)}</span>
                <button
                  data-testid={`draft-convert-${d.draft_number}`}
                  className="primary"
                  onClick={() => openInPos(d)}
                ><CheckCircle2 size={13} style={{ marginRight: 4 }} />
                  Open in POS
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Small inline banner for the POS page. Shows count + quick-enter field. */
export function DraftsBanner({ onOpenInPos }) {
  const [count, setCount] = useState(0);
  const [n, setN] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    const load = () => axios.get(`${API}/drafts`).then((r) => setCount((r.data || []).length)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const open = async () => {
    if (!/^\d{4}$/.test(n)) { setErr("Enter the 4-digit draft number"); return; }
    setErr("");
    try {
      const r = await axios.get(`${API}/drafts`);
      const found = (r.data || []).find((d) => d.draft_number === Number(n));
      if (!found) { setErr("Draft not found or expired"); return; }
      onOpenInPos?.(found);
      setN("");
    } catch (e) { setErr(fmtErr(e)); }
  };

  if (count === 0 && !n) return null;
  return (
    <div className="drafts-banner" data-testid="drafts-banner">
      <b>{count} draft{count === 1 ? "" : "s"} awaiting pickup</b>
      <input
        data-testid="drafts-banner-input"
        placeholder="Enter draft #"
        inputMode="numeric"
        value={n}
        onChange={(e) => setN(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
        onKeyDown={(e) => e.key === "Enter" && open()}
      />
      <button data-testid="drafts-banner-open" onClick={open} disabled={n.length !== 4}>
        Open
      </button>
      {err && <span className="drafts-banner-err">{err}</span>}
    </div>
  );
}
